import {
  FC, useMemo, useCallback,
} from 'react';
import { useTranslation } from 'next-i18next';
import Typography from 'src/common/components/Typography';
import {
  Field, FieldInputProps, useForm, useFormState,
} from 'react-final-form';
import { TripCartFormState } from 'src/listing/types';
import {
  FORM_DURATION, IS_MULTI_DAY,
  TRIP_CART_PANEL_CALENDAR,
} from 'src/listing/constants';
import Dropdown, { DropdownOption } from 'src/common/components/DropdownDS22';
import Label from 'src/common/components/Label';
import Icon, { IconIds } from 'src/common/components/IconDS22';
import classNames from 'classnames';
import getClassNames from 'src/common/utils/getClassNames';
import SkeletonBlock from 'src/common/components/SkeletonLoader/SkeletonBlock';
import { useTripCartOptionsContext } from 'src/trip-cart-deprecated/hooks/useTripCartOptionsContext';
import { useTripCartContext } from 'src/trip-cart-deprecated/hooks/useTripCartContext';
import useFeatureFlag from 'src/common/hooks/useFeatureFlag';
import { TripCartDurationOption } from 'src/trip-cart-deprecated/utils/tripCartUtils';
import TripCartAttention from 'src/trip-cart-deprecated/components/TripCartAttention';
import styles from './TripCartDuration.module.scss';

export type Props = {
  inSheet?: boolean;
  disable?: boolean;
  hideLabel?: boolean;
  showMultidayToggle?: boolean;
};

const TripCartDuration: FC<Props> = ({
  inSheet,
  disable,
  hideLabel,
  showMultidayToggle = true,
}) => {
  const [t] = useTranslation('listing-page');
  const form = useForm<TripCartFormState>();
  const { change } = form;
  const {
    values: {
      isMultiDay,
      formDuration,
    },
  } = useFormState<TripCartFormState>();
  const {
    showPanel,
  } = useTripCartContext();
  const { options: formOptions, optionsLoading } = useTripCartOptionsContext();
  const isSummaryEnabled = useFeatureFlag('unified-trip-cart--summary');
  const shouldAllowMultidayToggle = !isSummaryEnabled && formOptions.isMultiday.length > 1;

  const options: DropdownOption[] = useMemo(() => (
    formOptions.formDuration.map((option): DropdownOption => {
      const label = t(`trip-cart--duration-${option.unit}`, { duration: option.duration, count: option.duration });
      return {
        id: option.durationFormatted,
        selectedLabel: label,
        dropdownLabel: label,
        icon: option.isInstabook ? 'instabook-filled' : undefined,
      };
    })), [formOptions.formDuration, t]);

  const onChange = (option: DropdownOption<string>, input: FieldInputProps<any, HTMLElement>) => {
    input.onChange(option.id);
  };

  const handleCalendarOpen = useCallback(() => {
    showPanel(TRIP_CART_PANEL_CALENDAR);
  }, [showPanel]);

  const toggleMultiday = useCallback(async () => {
    const toMultiDay = !isMultiDay;
    if (toMultiDay) {
      handleCalendarOpen();
    }
    change(IS_MULTI_DAY, toMultiDay);
  }, [isMultiDay, change, handleCalendarOpen]);

  const getIcon = useCallback((item?: TripCartDurationOption): IconIds => {
    if (item?.isInstabook) return 'instabook';
    if (!isMultiDay) return 'clock';
    if (item?.unit === 'night') return 'moon';
    return 'duration';
  }, [isMultiDay]);

  const customItemRenderer = (dropdownOption?: DropdownOption) => {
    const { formDuration: durations } = formOptions;
    const durationOption = durations.find((x) => x.durationFormatted === dropdownOption?.id);
    return (
      <>
        {dropdownOption?.icon ? <Icon id={dropdownOption?.icon} size="m" />
          : (
            <Icon id={getIcon(durationOption)} size="m" />
          )}
        <span>{dropdownOption?.selectedLabel}</span>
      </>
    );
  };

  const getSelectedOption = useCallback((value: string): DropdownOption | undefined => {
    if (!value) return options[0];
    return options.find((option) => option.id === value);
  }, [options]);

  const selectedOption = useMemo(() => (
    getSelectedOption(formDuration)
  ), [formDuration, getSelectedOption]);

  const renderDropdown = (input: FieldInputProps<any, HTMLElement>) => {
    const selected = getSelectedOption(input.value);
    // Ensure the dropdown doesn't between controlled and uncontrolled
    if (!selected) {
      return null;
    }

    return (
      <Dropdown
        id="trip-details-duration"
        label={t('trip-cart--duration')}
        defaultOption={selected}
        selectedValue={selected}
        variant="white"
        options={options}
        fullWidth
        limitMenuHeight
        onChange={(option) => onChange(option, input)}
        iconSize="s"
        iconPosition="right"
        buttonSize="l"
        optionSize="m"
        selectedItemRenderer={customItemRenderer}
        data-test="duration"
        renderInPortal
      />
    );
  };

  const singleOptionIcon: IconIds | null = useMemo(() => {
    const { formDuration: durations } = formOptions;
    const firstOption = durations?.[0];

    // Only same day allows a dropdown
    return getIcon(firstOption);
  }, [formOptions, getIcon]);

  return (
    <div className={getClassNames(styles, 'root', classNames({ inSheet }))}>
      {!hideLabel && (
        <div className={styles.header}>
          <Label
            label={t('trip-cart--duration')}
            size={inSheet ? 'l' : 'm'}
            compact={inSheet}
          />
          {showMultidayToggle && shouldAllowMultidayToggle && !optionsLoading && (
            <button className={styles.multidayToggleButton} type="button" onClick={toggleMultiday} data-test="same-day-toggle">
              <Typography type="span" size="body-s">
                {isMultiDay ? t('trip-cart--multi-day-label') : t('trip-cart--same-day-label')}
              </Typography>
            </button>
          )}
        </div>
      )}
      {(
        disable === true
        || options.length === 1
        // !options.length can occur in previewMode when tiers are not yet set
        || !options.length)
        ? (
          <Field name={FORM_DURATION}>
            {() => (
              optionsLoading || !options.length
                ? <SkeletonBlock size={10} />
                : (
                  <button
                    type="button"
                    className={styles.durationText}
                    data-test="duration-link"
                  >
                    <TripCartAttention field="formDuration" />
                    <Typography
                      type="p"
                      size="body-l"
                      weight="semibold"
                    >
                      <span className={styles.multiDateDuration}>
                        {singleOptionIcon && (
                          <Icon
                            id={singleOptionIcon}
                            size="l"
                            className={formOptions.formDuration[0].isInstabook ? styles.instabookIcon : ''}
                          />
                        )}
                        {selectedOption?.selectedLabel}
                      </span>
                    </Typography>
                  </button>
                ))}
          </Field>
        )
        : (
          <div className={styles.dropdown}>
            <Field name={FORM_DURATION}>
              {({ input }) => (
                !optionsLoading
                  ? (
                    <>
                      <TripCartAttention field="formDuration" />
                      {renderDropdown(input)}
                    </>
                  )
                  : (
                    <SkeletonBlock size={10} />
                  ))}
            </Field>
          </div>
        )}
    </div>
  );
};

export default TripCartDuration;
